import { useEffect, useRef, useState } from "react";
import {
  MdClose,
  MdOutlineWhatsapp,
  MdOutlineEmail,
  MdCheckCircleOutline,
  MdOpenInNew,
  MdPushPin,
} from "react-icons/md";
import useCrmAccount from "./useCrmAccount";
import { Badge, SkeletonRows } from "./Primitives";
import {
  formatDate,
  formatDaysCount,
  formatMoney,
  formatNumber,
  formatRelativeDays,
  initialsOf,
  INTERACTION_LABELS,
  INTERACTION_TYPES,
  PLAN_TONE,
  whatsappLink,
} from "./crmHelpers";
import ClientLink from "./ClientLink";
import { useApplication } from "../../contexts/ApplicationContext";

/*
  Painel lateral de trabalho.

  É a peça que faltava. Antes, trabalhar uma fila de 14 contas era: clicar na
  conta, sair da lista, registrar o contato, voltar, procurar onde parou,
  clicar na próxima. Seis passos por conta, e o lugar na lista se perdia toda
  vez.

  Aqui a conta abre POR CIMA da lista. Registrar contato e criar tarefa
  acontecem no painel, e `onNext` avança para a próxima da mesma fila sem
  fechar — o fluxo vira "atender, ↓, atender".

  Continua existindo a ficha completa em `/clientes/:id`: o painel é para
  trabalhar a fila, a ficha é para estudar a conta.
*/

const QUICK_TASKS = [
  { label: "Retornar amanhã", days: 1 },
  { label: "Retornar em 3 dias", days: 3 },
  { label: "Retornar na semana que vem", days: 7 },
];

const addDaysIso = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};

const Stat = ({ label, value, tone = "text-slate-900" }) => (
  <div className="rounded-xl bg-slate-50 px-3 py-2">
    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
      {label}
    </p>
    <p className={`text-sm font-bold leading-tight ${tone}`}>{value}</p>
  </div>
);

const AccountDrawer = ({
  companyId,
  onClose,
  onNext,
  onPrev,
  position,
  total,
}) => {
  const { isOperator } = useApplication();
  const {
    account,
    usage,
    relationship,
    error,
    addInteraction,
    addTask,
    setTaskStatus,
    stageLabel,
  } = useCrmAccount(companyId);

  const [type, setType] = useState("CALL");
  const [body, setBody] = useState("");
  const [justSaved, setJustSaved] = useState(false);
  const textareaRef = useRef(null);

  // Trocar de conta no modo "trabalhar a fila" precisa limpar o rascunho —
  // senão a anotação da conta anterior vai parar na próxima.
  useEffect(() => {
    setBody("");
    setJustSaved(false);
    setType("CALL");
  }, [companyId]);

  // Esc fecha. O painel cobre a lista, então sair tem que ser mais barato do
  // que mirar no X.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const save = async () => {
    if (!body.trim()) return;
    await addInteraction.mutateAsync({ type, body });
    setBody("");
    setJustSaved(true);
  };

  /*
    Registrar o contato e já ir para a próxima é o movimento mais repetido do
    dia. Quando não há rascunho escrito, o botão só avança — serve para pular
    quem não atendeu.
  */
  const saveAndNext = async () => {
    if (body.trim()) await save();
    onNext?.();
  };

  const phone = whatsappLink(
    usage?.phoneNumber || account?.company?.phoneNumber,
  );

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden
      />

      <div className="fixed right-0 top-0 z-50 flex h-screen w-full max-w-[460px] flex-col bg-white shadow-2xl">
        <header className="flex items-start gap-3 border-b border-slate-200 px-5 py-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-pink-50 text-xs font-bold text-pink-700">
            {initialsOf(account?.company?.name)}
          </span>

          <div className="min-w-0 flex-1">
            <p className="truncate text-base font-bold leading-tight text-slate-900">
              {account?.company?.name || "Carregando..."}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {usage ? (
                <>
                  <Badge tone={PLAN_TONE[usage.planId] || "neutral"}>
                    {usage.planId}
                  </Badge>
                  <Badge tone={usage.stageTone}>{usage.stageLabel}</Badge>
                </>
              ) : null}
              <ClientLink
                hideWhenBlocked
                to={`/clientes/${companyId}`}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-pink-700 hover:underline"
              >
                ficha completa <MdOpenInNew size={12} />
              </ClientLink>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            title="Fechar (Esc)"
            className="shrink-0 rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <MdClose size={20} />
          </button>
        </header>

        {relationship.isLoading ? (
          <div className="p-5">
            <SkeletonRows rows={4} />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {error ? (
              <p className="mb-3 rounded-xl bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-800 ring-1 ring-inset ring-rose-200">
                {error}
              </p>
            ) : null}

            <div className="flex gap-2">
              {phone ? (
                <a
                  href={phone}
                  target="_blank"
                  rel="noreferrer"
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-600 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700"
                >
                  <MdOutlineWhatsapp size={17} /> WhatsApp
                </a>
              ) : null}
              {usage?.email ? (
                <a
                  href={`mailto:${usage.email}`}
                  className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-pink-200"
                >
                  <MdOutlineEmail size={17} />
                </a>
              ) : null}
            </div>

            <div className="mt-3 grid grid-cols-3 gap-2">
              <Stat
                label="Contato"
                value={
                  account?.lastContactAt
                    ? formatRelativeDays(account.lastContactAt)
                    : "nunca"
                }
                tone={
                  account?.lastContactAt ? "text-slate-900" : "text-rose-600"
                }
              />
              {/* Vêm da listagem de clientes, que o operador não tem: sem este
                  corte ele veria "R$ 0,00" pago em toda conta. */}
              {isOperator ? null : (
                <>
                  <Stat
                    label="Agend. 30d"
                    value={formatNumber(usage?.appointmentsLast30Days || 0)}
                  />
                  <Stat
                    label="Cadastro"
                    value={formatDaysCount(usage?.daysSinceRegistration)}
                  />
                  <Stat
                    label="Mensal"
                    value={formatMoney(usage?.monthlyValue || 0)}
                  />
                  <Stat label="Pago" value={formatMoney(usage?.totalPaid || 0)} />
                  <Stat
                    label="Vencimento"
                    value={
                      usage?.planExpirationDate
                        ? formatRelativeDays(usage.planExpirationDate)
                        : "—"
                    }
                    tone={
                      usage?.daysToExpire < 0
                        ? "text-rose-600"
                        : usage?.daysToExpire <= 7
                          ? "text-amber-600"
                          : "text-slate-900"
                    }
                  />
                </>
              )}
            </div>

            {account?.pinned?.length ? (
              <div className="mt-3 rounded-xl border border-pink-200 bg-pink-50/60 p-3">
                <p className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-pink-700">
                  <MdPushPin size={12} /> Leia antes
                </p>
                {account.pinned.map((note) => (
                  <p key={note.id} className="text-sm text-slate-800">
                    {note.body}
                  </p>
                ))}
              </div>
            ) : null}

            <div className="mt-4">
              <div className="flex flex-wrap gap-1.5">
                {INTERACTION_TYPES.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setType(option.value)}
                    className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset transition ${
                      type === option.value
                        ? "bg-pink-600 text-white ring-pink-600"
                        : "bg-white text-slate-600 ring-slate-200 hover:ring-pink-200"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <textarea
                ref={textareaRef}
                value={body}
                onChange={(event) => {
                  setBody(event.target.value);
                  setJustSaved(false);
                }}
                rows={3}
                placeholder="O que ela disse, o que ficou combinado..."
                className="mt-2 w-full resize-y rounded-xl border border-slate-200 p-3 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
              />

              {justSaved ? (
                <p className="text-xs font-semibold text-emerald-700">
                  Contato registrado.
                </p>
              ) : null}

              <div className="mt-1 flex gap-2">
                <button
                  type="button"
                  onClick={save}
                  disabled={!body.trim() || addInteraction.isLoading}
                  className="flex-1 rounded-xl bg-pink-600 py-2 text-sm font-semibold text-white transition hover:bg-pink-700 disabled:opacity-40"
                >
                  {addInteraction.isLoading ? "Salvando..." : "Registrar"}
                </button>
              </div>
            </div>

            <div className="mt-4">
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Agendar retorno
              </p>
              {/*
                Três botões em vez de um formulário com data e prioridade. O
                compromisso que nasce de uma ligação é quase sempre "falo com
                ela de novo em alguns dias" — pedir título, data e prioridade
                para isso fazia ninguém criar tarefa nenhuma.
              */}
              <div className="flex flex-wrap gap-1.5">
                {QUICK_TASKS.map((quick) => (
                  <button
                    key={quick.days}
                    type="button"
                    disabled={addTask.isLoading}
                    onClick={() =>
                      addTask.mutate({
                        title: `Retomar contato — ${
                          account?.company?.name || "cliente"
                        }`,
                        dueDate: addDaysIso(quick.days),
                        priority: "NORMAL",
                      })
                    }
                    className="rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-pink-300 hover:text-pink-700 disabled:opacity-40"
                  >
                    {quick.label}
                  </button>
                ))}
              </div>
            </div>

            {account?.openTasks?.length ? (
              <div className="mt-4">
                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                  Tarefas abertas
                </p>
                <ul className="space-y-1.5">
                  {account.openTasks.map((task) => (
                    <li
                      key={task.id}
                      className="flex items-start gap-2 rounded-xl bg-slate-50 p-2"
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setTaskStatus.mutate({
                            taskId: task.id,
                            status: "DONE",
                          })
                        }
                        title="Marcar como feita"
                        className="mt-0.5 text-slate-300 transition hover:text-emerald-600"
                      >
                        <MdCheckCircleOutline size={17} />
                      </button>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-slate-800">
                          {task.title}
                        </p>
                        <p className="text-[11px] text-slate-500">
                          {task.assigneeName}
                          {task.dueDate
                            ? ` · ${formatRelativeDays(task.dueDate)}`
                            : ""}
                        </p>
                      </div>
                      {task.isOverdue ? (
                        <Badge tone="danger">atrasada</Badge>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="mt-4">
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Histórico
              </p>
              {account?.timeline?.length ? (
                <ul className="space-y-2.5 border-l border-slate-200 pl-3">
                  {account.timeline.slice(0, 12).map((item) => (
                    <li key={`${item.kind}-${item.id}`} className="relative">
                      <span className="absolute -left-[17px] top-1.5 h-2 w-2 rounded-full bg-slate-300" />
                      <p className="text-xs text-slate-800">
                        {item.kind === "INTERACTION" ? (
                          <>
                            <span className="font-semibold">
                              {INTERACTION_LABELS[item.type] || item.type}
                            </span>{" "}
                            {item.body}
                          </>
                        ) : item.kind === "TASK" ? (
                          <>
                            <span className="font-semibold">Tarefa</span>{" "}
                            {item.title}
                          </>
                        ) : (
                          <>
                            <span className="font-semibold">Estágio</span> →{" "}
                            {stageLabel(item.stage)}
                          </>
                        )}
                      </p>
                      <p className="text-[10px] text-slate-400">
                        {formatDate(item.at)} · {item.authorName}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-slate-400">
                  Nenhum registro ainda — este é o primeiro contato com ela.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AccountDrawer;
